<template>
  <div class="meeting-attendee-row" data-testid="meeting-attendee-row">
    <div class="meeting-attendee-row__avatar" data-testid="meeting-attendee-avatar">{{ initials }}</div>
    <div class="meeting-attendee-row__identity">
      <div class="meeting-attendee-row__name-line">
        <span class="meeting-attendee-row__name" data-testid="meeting-attendee-name">{{ displayName }}</span>
        <span v-if="attendee.isOrganizer" class="meeting-attendee-row__organizer-badge" data-testid="meeting-attendee-organizer-badge">Organizer</span>
      </div>
      <span v-if="attendee.name" class="meeting-attendee-row__email" data-testid="meeting-attendee-email">{{ attendee.email }}</span>
    </div>
    <span v-if="sent" class="meeting-attendee-row__sent" data-testid="meeting-attendee-sent">
      <Icon name="general.tick" size="sm" />
      <span>Sent</span>
    </span>
    <Button
      v-else-if="canSend"
      type="ghost"
      variant="gray"
      icon="social.mail"
      icon-size="base"
      :loading="sending"
      :disabled="sending"
      :aria-label="`Send recording link to ${attendee.email}`"
      data-testid="meeting-attendee-send-btn"
      @click="emit('send', attendee.email)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Button, Icon } from '@/storybook';
import { MeetingAttendee } from '@/modules/viewer/store/useViewerItemStore';

const props = withDefaults(
  defineProps<{
    attendee: MeetingAttendee;
    canSend?: boolean;
    sending?: boolean;
    sent?: boolean;
  }>(),
  {
    canSend: true,
    sending: false,
    sent: false,
  }
);

const emit = defineEmits<{
  send: [email: string];
}>();

const displayName = computed(() => props.attendee.name || props.attendee.email);

const initials = computed(() => {
  const name = props.attendee.name?.trim();

  if (name) {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map(part => part[0])
      .join('')
      .toUpperCase();
  }

  return props.attendee.email.slice(0, 2).toUpperCase();
});
</script>

<style scoped>
.meeting-attendee-row {
  @apply flex items-center gap-3 py-2 border-b border-zinc-100 dark:border-zinc-700 last:border-b-0;
}

.meeting-attendee-row__avatar {
  @apply flex items-center justify-center w-8 h-8 shrink-0 rounded-full bg-zinc-100 dark:bg-zinc-700 text-xs font-semibold text-zinc-600 dark:text-zinc-300;
}

.meeting-attendee-row__identity {
  @apply flex flex-col flex-1 min-w-0;
}

.meeting-attendee-row__name-line {
  @apply flex items-center gap-2 min-w-0;
}

.meeting-attendee-row__name {
  @apply text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate;
}

.meeting-attendee-row__organizer-badge {
  @apply shrink-0 px-1.5 py-0.5 text-xs font-medium rounded-full bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400;
}

.meeting-attendee-row__email {
  @apply text-xs text-zinc-500 dark:text-zinc-400 truncate;
}

.meeting-attendee-row__sent {
  @apply flex items-center gap-1 shrink-0 px-2 text-xs font-medium text-green-600 dark:text-green-300;
}
</style>
